import { Component, HostBinding, ViewEncapsulation, OnDestroy } from '@angular/core';
import { Subscription, fromEvent, concatMap, from, of, catchError, map } from 'rxjs';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
	GridApi, GridReadyEvent,
	ICellRendererParams,
	ViewportChangedEvent
} from '@ag-grid-community/core';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { OrganizationService } from '../../../services/organization.service';
import { CommonGridOptions, clientSideAgGridModules } from '../../../../shared/datacenter/grid-options';
import { TranslationService } from '../../../../shared/services/translation.service';
import { CommonColDef, LinkColDef, IconColDef } from '../../../../shared/datacenter/column-defs';
import { OrganizationUserInfo } from '../../../models/organization-user-info';
import { OrderBy, OrderByDirection } from '../../../../shared/utils/constant';
import { CheckboxHeaderComponent } from '../../../../shared/datacenter/header-components/checkbox-header.component';
import { ColumnType } from '../../../../shared/datacenter/column-type';
import { DatacenterTextFilterComponent }
	from '../../../../shared/datacenter/filter-type/datacenter-text-filter.component';
import { DatacenterDropdownFilterComponent }
	from '../../../../shared/datacenter/filter-type/datacenter-dropdown-filter.component';
import filterIcon from '../../../../shared/svg/filters.svg';
import { UserService } from '../../../../shared/services/user.service';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { ManageUserStatusComponent }
	from '../../../cell-renderers/manage-user-status-cell/manage-user-status.component';
import { UserCardService } from '../../../services/user-card.service';

import { ManageUserRoleComponent } from './manage-user-role/manage-user-role.component';

const resizeObservable = fromEvent(globalThis, 'resize');

const linkToUserCardCellRendererParams = {
	routerLink(params: ICellRendererParams<OrganizationUserInfo, unknown>) {
		const user = params.data;
		if (!user) {
			return undefined;
		}
		return {
			commands: [
				'/user',
				'card',
				user.id,
				'settings',
				'general'
			]
		};
	}
};

@Component({
	selector: 'copo-organization-card-users',
	templateUrl: './organization-card-users.component.html',
	styleUrl: './organization-card-users.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,

		MatButtonModule,
		RouterLink,
		TranslateModule,
		MatIconModule,
		AgGridModule,
		LoadingSpinnerComponent
	]
})
export class OrganizationCardUsersComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	gridOptions?: CommonGridOptions<OrganizationUserInfo>;
	fetched = false;
	page = 1;
	limit = 5000;
	sortWay = OrderByDirection.DESC;
	sortBy: keyof OrganizationUserInfo = 'id';

	readonly modules = clientSideAgGridModules;
	users: OrganizationUserInfo[] | undefined;
	userClone: OrganizationUserInfo[] = [];
	updatedPersonMembers: OrganizationUserInfo[] = [];
	isEditMode = false;
	isSaving = false;
	isSupplierOrganization = false;
	fields = [
		'id',
		'firstName',
		'lastName',
		'address.email',
		'personMembers.role',
		'personMembers.status'
	];

	private organizationId?: string;
	private readonly subscriptions: Subscription[] = [];
	private orgUsersServiceSub: Subscription = Subscription.EMPTY;
	private organizationDetailsSub: Subscription = Subscription.EMPTY;
	private roleSub: Subscription = Subscription.EMPTY;
	private readonly routeSub: Subscription = Subscription.EMPTY;
	private readonly dialogSubscription: Subscription = new Subscription();
	private gridApi?: GridApi;
	private readonly removeUserSub?: Subscription;

	constructor(
		private readonly organizationService: OrganizationService,
		private readonly route: ActivatedRoute,
		private readonly translationService: TranslationService,

		readonly dialog: MatDialog,
		private readonly snackBar: MatSnackBar,
		private readonly userService: UserService,
		private readonly userCardService: UserCardService
	) {
		if (this.route.parent === null) {
			return;
		}
		this.routeSub = this.route.parent.paramMap.subscribe(params => {
			const orgId = params.get('id');
			if (orgId === null) {
				return;
			}
			this.organizationId = orgId;
			this.loadOrganizationContext();
		});
	}

	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		params.api.sizeColumnsToFit();
		this.subscriptions.push(resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		}));
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	getOrganizationUsers() {
		if (!this.organizationId) {
			return;
		}
		this.orgUsersServiceSub = this.organizationService.getOrganizationUsers(
			this.organizationId,
			this.fields,
			'',
			this.page,
			this.limit,
			new OrderBy(this.sortBy, this.sortWay),
			false,
			false
		)
			.subscribe({
				next: organizationUsers => {
					this.users = this.filterUsersForGrid(organizationUsers.results);
					if (this.users.length === 0) {
						this.fetched = true;
						return;
					}
					this.initAgGrid(this.users);
					if (this.gridApi) {
						this.gridApi.setGridOption('rowData', this.users);
					}
					this.fetched = true;
				},
				error: (err: unknown) => {
					this.fetched = true;
					console.error('Error while fetching organization users', err);
				}
			});
	}

	onEditRole() {
		if (this.isSaving) {
			return;
		}
		if (this.users) {
			this.userClone = this.cloneUsers(this.users);
		}

		this.isEditMode = true;
		if (this.users === undefined) {
			return;
		}

		if (this.gridApi) {
			this.updateRoleColumnContext();
			this.gridApi.setGridOption('rowData', this.users);
		}
	}

	onRoleChange(updatedRows: OrganizationUserInfo[], newRole: string) {
		const originalUsers = this.users;
		if (!originalUsers) {
			return;
		}
		const normalizedNewRole = this.normalizeRole(newRole);
		for (const updatedMember of updatedRows) {
			const originalMember = originalUsers.find(
				(member: OrganizationUserInfo) => member.id === updatedMember.id
			);
			const index = this.updatedPersonMembers.findIndex(
				(member: OrganizationUserInfo) => member.id === updatedMember.id
			);

			// Create a shallow copy of updatedMember and update the role in the clone
			const clonedMember = { ...updatedMember, personMembers: { ...updatedMember.personMembers } };
			clonedMember.personMembers.role = normalizedNewRole;

			// If the role has changed from the original, add/update it in the updatedPersonMembers
			if (originalMember && this.normalizeRole(originalMember.personMembers.role) !== normalizedNewRole) {
				if (index === -1) {
					// If not in the list, add it
					this.updatedPersonMembers.push(clonedMember);
				} else {
					// If already in the list, update the record
					this.updatedPersonMembers[index] = clonedMember;
				}
			} else if (originalMember
				&& this.normalizeRole(originalMember.personMembers.role) === normalizedNewRole
				&& index !== -1) {
				this.updatedPersonMembers.splice(index, 1);
			}
		}
	}

	onSaveUserRoleForm() {
		if (this.isSaving) {
			return;
		}
		if (this.users === undefined) {
			return;
		}

		// If no updates were made, cancel and return
		if (this.updatedPersonMembers.length === 0) {
			this.onCancelUserRoleForm();
			return;
		}

		const usersToUpdateList = this.cloneUsers(this.users);
		if (usersToUpdateList.length === 0) {
			return;
		}

		for (const updatedMember of this.updatedPersonMembers) {
			const index = usersToUpdateList.findIndex(user => user.id === updatedMember.id);
			if (index !== -1) {
				// Update the original list with the updated member
				usersToUpdateList[index] = updatedMember;
			}
		}

		// Now the users list contains the updated roles, so we can validate the administrators
		// Filter for users with the 'Administrator' role
		const adminUsers = usersToUpdateList.filter(user =>
			this.isAdminRole(user.personMembers.role) && this.isActiveForAdminValidation(user));

		// Validation checks
		if (adminUsers.length === 0) {
			this.snackBar.open(
				this.translationService.getTR('organization-users.one-admin'),
				undefined,
				{
					duration: 2000,
				}
			);
			return;
		} else if (adminUsers.length > 1) {
			this.snackBar.open(
				this.translationService.getTR('organization-users.one-admin-only'),
				undefined,
				{
					duration: 3000,
				}
			);
			return;
		}

		// Proceed with the save logic if validation passes
		// Sort updatedPersonMembers so that the user with the 'dsu' role is first
		const sortedUpdatedMembers = [...this.updatedPersonMembers].sort((_, b) =>
			this.isAdminRole(b.personMembers.role) ? 1 : -1);

		const results: { success: boolean }[] = [];
		this.isSaving = true;

		// Create an observable that processes the requests sequentially
		this.roleSub = from(sortedUpdatedMembers).pipe(
			concatMap(updatedMember =>
				this.userCardService.updateUserRole(
					Number(this.organizationId),
					updatedMember.id,
					updatedMember.personMembers.role
				).pipe(
					map(() => ({ success: true })),
					catchError((error: unknown) => {
						console.error(`Failed to update role for user ID: ${ updatedMember.id }`, error);
						return of({ success: false, memberId: updatedMember.id }); // Capture error status
					})
				))
		).subscribe({
			next: result => {
				// Collect results for each member
				results.push(result);
			},
			complete: () => {
				// Check if all updates succeeded
				const updateSucceed = results.every(result => result.success);

				if (updateSucceed) {
					this.users = usersToUpdateList;
					this.userClone = this.cloneUsers(this.users);
					this.snackBar.open(
						this.translationService.getTR('organization-users.roles-saved'),
						undefined,
						{ duration: 2000 }
					);
				} else {
					this.snackBar.open(
						this.translationService.getTR('organization-users.roles-error'),
						undefined,
						{ duration: 2000 }
					);
					console.error('Failed members:', results.filter(result => !result.success));
					this.getOrganizationUsers();
				}

				this.updatedPersonMembers = [];
				this.isEditMode = false;
				if (this.gridApi) {
					this.updateRoleColumnContext();
					if (this.users) {
						this.gridApi.setGridOption('rowData', this.users);
					}
				}
				this.isSaving = false;
			}
		});
	}

	onCancelUserRoleForm() {
		if (this.isSaving) {
			return;
		}
		if (this.users === undefined) {
			return;
		}
		if (this.userClone.length > 0) {
			this.users = this.cloneUsers(this.userClone);
		}
		this.updatedPersonMembers = [];

		this.isEditMode = false;

		if (this.gridApi) {
			this.updateRoleColumnContext();
			this.gridApi.setGridOption('rowData', this.users);
		}
	}

	ngOnDestroy() {
		for (const sub of this.subscriptions) {
			sub.unsubscribe();
		}
		this.routeSub.unsubscribe();
		this.orgUsersServiceSub.unsubscribe();
		this.organizationDetailsSub.unsubscribe();
		this.dialogSubscription.unsubscribe();
		this.removeUserSub?.unsubscribe();
		this.roleSub.unsubscribe();
	}

	isAdmin() {
		const loginUser = this.userService.loggedUser;
		if (loginUser) {
			return Boolean(loginUser.canUpdateOrganizationUsers(Number(this.organizationId)));
		}
		return false;
	}

	isAdminAndDeligationCheck() {
		const loginUser = this.userService.loggedUser;
		// Admin check
		if (loginUser && this.isAdmin()) {
			return !loginUser.isDelegating();
		}
		return false;
	}

	getIcon(status: string) {
		if (status === 'pend') {
			return {
				iconName: 'md:done',
			};
		}
		return '';
	}

	private initAgGrid(users: OrganizationUserInfo[]) {
		this.gridOptions = new CommonGridOptions<OrganizationUserInfo>({
			translationService: this.translationService,
			domLayout: 'normal',
			icons: { filter: filterIcon },
			rowData: users,
			headerHeight: 70,
			columnDefs: [
				new CommonColDef<OrganizationUserInfo>({
					headerName: this.translationService.getTR('organization.user-id'),
					headerComponent: CheckboxHeaderComponent,
					field: 'id',
					valueGetter: params => String(params.data?.id ?? ''),
					filter: DatacenterTextFilterComponent,
					maxWidth: 90,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: true,
				}),
				new LinkColDef<OrganizationUserInfo>({
					headerName: this.translationService.getTR('settings.name'),
					headerComponent: CheckboxHeaderComponent,
					sort: 'asc',
					maxWidth: 400,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const organizationUser = params.data;
						return `${ organizationUser.firstName } ${ organizationUser.lastName }`;
					},
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					cellRendererParams: linkToUserCardCellRendererParams,
					sortable: true
				}),
				new CommonColDef<OrganizationUserInfo>({
					headerName: this.translationService.getTR('settings.email'),
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const organizationUser = params.data;
						return organizationUser.address.email;
					},
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					headerComponent: CheckboxHeaderComponent,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: true
				}),
				new CommonColDef<OrganizationUserInfo>({
					headerName: this.translationService.getTR('settings.organizations.role'),
					colId: 'role',
					headerComponent: CheckboxHeaderComponent,
					maxWidth: 120,
					valueGetter: params => this.getRoleLabel(params.data),
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						userFields: ['personMembers.role'],
						removeNoValueFilter: true
					},
					suppressHeaderMenuButton: false,
					sortable: true,
					cellRenderer: ManageUserRoleComponent,
					cellRendererParams: {
						isEditMode: this.isEditMode,
						isSupplierOrganization: this.isSupplierOrganization,
						organizationCardUsersList: this.users,
						onRoleChangeCallback: this.onRoleChange.bind(this)
					},
				}),
				new CommonColDef<OrganizationUserInfo>({
					headerName: this.translationService.getTR('organization.user-status-in-organization'),
					colId: 'status',
					headerComponent: CheckboxHeaderComponent,
					maxWidth: 400,
					valueGetter: params => this.getUserStatusLabel(params.data),
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						userFields: ['personMembers.status', 'status'],
						removeNoValueFilter: true
					},
					suppressHeaderMenuButton: false,
					cellStyle: params => this.getFontColorByStatus(params.data),
					sortable: true
				}),
				new CommonColDef<OrganizationUserInfo>({
					headerName: this.translationService.getTR('datacenter-filter.user-status'),
					colId: 'personStatus',
					headerComponent: CheckboxHeaderComponent,
					maxWidth: 400,
					valueGetter: params => this.getPersonStatusLabel(params.data),
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						userFields: ['status'],
						removeNoValueFilter: true
					},
					suppressHeaderMenuButton: false,
					cellStyle: params => this.getFontColorByPersonStatus(params.data),
					sortable: true
				}),
				new CommonColDef<OrganizationUserInfo>({
					headerName: this.translationService.getTR('user.validationDaysLeft'),
					headerComponent: CheckboxHeaderComponent,
					maxWidth: 400,
					valueGetter: params => this.getValidationDaysLabel(params.data),
					suppressHeaderMenuButton: false,
					filter: DatacenterTextFilterComponent,
					comparator: (valueA: unknown, valueB: unknown) =>
						this.getValidationDaysSortValue(valueA) - this.getValidationDaysSortValue(valueB),
					cellStyle: params => {
						if (params.value === this.translationService.getTR('user.expired')) {
							return { color: 'red' };
						}
						return null;
					},
					sortable: true
				}),
				new IconColDef<OrganizationUserInfo>({
					headerName: this.translationService.getTR('organization.user-manage'),
					headerComponent: CheckboxHeaderComponent,
					maxWidth: 70,
					minWidth: 70,
					width: 70,
					cellRenderer: ManageUserStatusComponent,
					cellRendererParams: {
						organizationId: this.organizationId,
						usersList: this.users,
						getOrganizationUsersList: this.getOrganizationUsers.bind(this)
					},
				}),
			]
		});
	}

	private cloneUsers(users: OrganizationUserInfo[]): OrganizationUserInfo[] {
		return users.map(user => ({ ...user, personMembers: { ...user.personMembers } }));
	}

	private filterUsersForGrid(users: OrganizationUserInfo[]): OrganizationUserInfo[] {
		return users.filter(user => {
			const personMemberStatus = user.personMembers.status.toLowerCase();
			const personStatus = user.status.toLowerCase();
			return personStatus === 'dact' || (personMemberStatus !== 'disab' && personStatus !== 'disab');
		});
	}

	private getFontColorByStatus(organizationUserInfo?: OrganizationUserInfo): { color?: string } {
		if (!organizationUserInfo) {
			return { color: 'red' };
		}
		const status = this.getUserStatusCode(organizationUserInfo);
		switch (status) {
		case 'activ':
			return { color: 'green' };
		case 'pend':
			return { color: 'grey' };
		case 'pendingUserValidation':
			return { color: 'orange' };
		default:
			return { color: 'red' };
		}
	}

	private loadOrganizationContext() {
		if (!this.organizationId) {
			return;
		}

		this.organizationDetailsSub.unsubscribe();
		this.organizationDetailsSub = this.organizationService.findOrganizationDetails(this.organizationId)
			.subscribe({
				next: organization => {
					this.isSupplierOrganization = this.isSupplierRole(organization.role);
					this.getOrganizationUsers();
					this.updateRoleColumnContext();
				},
				error: (err: unknown) => {
					this.isSupplierOrganization = false;
					console.error('Error while fetching organization role context', err);
					this.getOrganizationUsers();
					this.updateRoleColumnContext();
				}
			});
	}

	private isSupplierRole(role?: string): boolean {
		const normalizedRole = (role ?? '').trim().toLowerCase();
		const supplierRoles = new Set(['supplier', 'sell', 'selag', 'suser', 'agcy', 'labo']);
		return supplierRoles.has(normalizedRole);
	}

	private updateRoleColumnContext() {
		if (!this.gridApi) {
			return;
		}
		const column = this.gridApi.getColumnDef('role');
		if (!column) {
			return;
		}

		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		column.cellRendererParams = {
			...column.cellRendererParams,
			isEditMode: this.isEditMode,
			isSupplierOrganization: this.isSupplierOrganization,
			organizationCardUsersList: this.users,
		};
		this.gridApi.refreshCells({ force: true });
	}

	private normalizeRole(role?: string): string {
		return (role ?? '').trim().toLowerCase();
	}

	private isAdminRole(role?: string): boolean {
		return this.normalizeRole(role) === 'dsu';
	}

	private isActiveForAdminValidation(user: OrganizationUserInfo): boolean {
		const personMemberStatus = this.normalizeStatus(user.personMembers.status);
		const personStatus = this.normalizeStatus(user.status);
		return personMemberStatus === 'activ' && personStatus === 'activ';
	}

	private normalizeStatus(status?: string): string {
		return (status ?? '').trim().toLowerCase();
	}

	private getRoleLabel(user?: OrganizationUserInfo): string {
		const role = this.normalizeRole(user?.personMembers.role);
		if (!role) {
			return '';
		}
		return this.translationService.getTR(`organizationrole.${ role }`);
	}

	private getUserStatusCode(user?: OrganizationUserInfo):
		'activ' | 'pend' | 'pendingUserValidation' | 'dact' | 'disab' {
		const personMemberStatus = this.normalizeStatus(user?.personMembers.status);
		const personStatus = this.normalizeStatus(user?.status);

		if (personMemberStatus === 'disab' || personStatus === 'disab') {
			return 'disab';
		}
		if (personStatus === 'dact' || personMemberStatus === 'dact') {
			return 'dact';
		}
		if (personMemberStatus === 'pend') {
			return 'pendingUserValidation';
		}
		if (personStatus === 'pend') {
			return 'pend';
		}
		return 'activ';
	}

	private getUserStatusLabel(user?: OrganizationUserInfo): string {
		const status = this.getUserStatusCode(user);
		switch (status) {
		case 'dact':
			return this.translationService.getTR('organization.status.deactivated');
		case 'pendingUserValidation':
			return this.translationService.getTR('issue.pending');
		case 'pend':
			return this.translationService.getTR('wait.user.validation');
		case 'activ':
			return this.translationService.getTR('active');
		default:
			return this.translationService.getTR('organization.status.disabled');
		}
	}

	private getPersonStatusCode(user?: OrganizationUserInfo): 'activ' | 'dact' | 'pend' | 'del' {
		const personStatus = this.normalizeStatus(user?.status);
		switch (personStatus) {
		case 'dact':
			return 'dact';
		case 'pend':
			return 'pend';
		case 'del':
			return 'del';
		default:
			return 'activ';
		}
	}

	private getPersonStatusLabel(user?: OrganizationUserInfo): string {
		switch (this.getPersonStatusCode(user)) {
		case 'dact':
			return this.translationService.getTR('organization.status.deactivated');
		case 'pend':
			return this.translationService.getTR('issue.pending');
		case 'del':
			return this.translationService.getTR('organization.status.deleted');
		default:
			return this.translationService.getTR('active');
		}
	}

	private getFontColorByPersonStatus(user?: OrganizationUserInfo): { color: string } {
		switch (this.getPersonStatusCode(user)) {
		case 'dact':
			return { color: 'red' };
		case 'pend':
			return { color: 'orange' };
		case 'del':
			return { color: 'red' };
		default:
			return { color: 'green' };
		}
	}

	private getValidationDaysDiff(user?: OrganizationUserInfo): number | null {
		if (!user?.expiryDate) {
			return null;
		}
		const status = this.getUserStatusCode(user);
		if (status !== 'pendingUserValidation' && status !== 'pend') {
			return null;
		}

		const expiryDate = new Date(user.expiryDate);
		const diffInMs = expiryDate.getTime() - Date.now();
		const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
		return Number.isNaN(diffInDays) ? null : diffInDays;
	}

	private getValidationDaysLabel(user?: OrganizationUserInfo): string {
		const diffInDays = this.getValidationDaysDiff(user);
		if (diffInDays === null) {
			return '';
		}
		return diffInDays > 0 ? `${ diffInDays } day(s) left` : this.translationService.getTR('user.expired');
	}

	private getValidationDaysSortValue(value: unknown): number {
		const normalizedValue = typeof value === 'string' ? value : '';
		if (!normalizedValue) {
			return Number.POSITIVE_INFINITY;
		}
		if (normalizedValue === this.translationService.getTR('user.expired')) {
			return Number.NEGATIVE_INFINITY;
		}
		const parsedValue = Number.parseInt(normalizedValue, 10);
		return Number.isNaN(parsedValue) ? Number.POSITIVE_INFINITY : parsedValue;
	}
}
